import React, { useId } from 'react'
import cn from 'classnames'
import { TierHierarchy } from '@mc/api'
import {
  Badge,
  IconCheckDefault,
  Separator,
  type BadgeKindValue,
} from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { getNumberOfInstructors } from '@mc/instructors'
import { PlansTier, useBenefitTranslation, usePlansContext } from '@mc/plans-ui'
import { type Variant2Layout } from '../../../prcng-1266_v1_cm_dual_cta'
import { useMCProCertsCatalogValue } from '../../hooks/useMCProCertsCatalogValue'
import { MC_PRO_TIER_I18N_NAMESPACE } from '../../i18n'
import { FAQ_UUID } from '../CertificatesFAQ'
import classes from './StackedTiles.module.scss'

const COPY_KEYS: Record<
  TierHierarchy,
  { subtitles: string[]; features: string[] }
> = {
  [TierHierarchy.INDIVIDUAL]: {
    subtitles: ['classes', 'devices'],
    features: ['access'],
  },
  [TierHierarchy.DUO]: {
    subtitles: ['devices', 'offline'],
    features: ['access', 'offline'],
  },
  [TierHierarchy.FAMILY]: {
    subtitles: ['devices'],
    features: ['access', 'offline'],
  },
  [TierHierarchy.FAMILY_PLUS]: {
    subtitles: ['certificates'],
    features: ['access'],
  },
}

const DEVICE_COPY_KEYS = ['devices', 'access']

type TileBadge = { key: string; kind?: BadgeKindValue }

const BEST_VALUE: TileBadge = { key: 'stackedTiles.bestValue' }

export const BADGES: Record<
  Variant2Layout,
  Partial<Record<TierHierarchy, TileBadge>>
> = {
  current: { [TierHierarchy.FAMILY_PLUS]: BEST_VALUE },
  premium_best_value: {
    [TierHierarchy.FAMILY]: BEST_VALUE,
    [TierHierarchy.FAMILY_PLUS]: {
      key: 'stackedTiles.forProfessionals',
      kind: 'dim',
    },
  },
  pro_best_value: {
    [TierHierarchy.DUO]: { key: 'stackedTiles.mostPopular' },
    [TierHierarchy.FAMILY_PLUS]: BEST_VALUE,
  },
}

const openCertificatesFaq = () => {
  if (typeof window === 'undefined') return

  const heading = window.document.querySelector<HTMLElement>(
    `[data-uuid="${FAQ_UUID}"]`,
  )
  if (!heading) return

  const scrollToHeading = () => heading.scrollIntoView({ behavior: 'smooth' })

  const button = heading.querySelector<HTMLButtonElement>(
    'button[aria-expanded="false"]',
  )
  if (button) {
    button.focus({ preventScroll: true })
    button.click()
    // Wait for the accordion expansion (200ms) to finish; otherwise the
    // in-flight animation interrupts the smooth scroll.
    window.setTimeout(scrollToHeading, 250)
  } else {
    scrollToHeading()
  }
}

const Feature = ({
  children,
  isSelected,
}: {
  children: React.ReactNode
  isSelected: boolean
}) => (
  <li
    className={cn('d-flex mc-gap-2', {
      'mc-text-color--medium': isSelected,
      'mc-text-color--tint': !isSelected,
    })}
  >
    <IconCheckDefault className='mc-mt-1 flex-shrink-0' size={2} />
    <div>{children}</div>
  </li>
)

const CertificatesFeature = ({ isSelected }: { isSelected: boolean }) => {
  const value = useMCProCertsCatalogValue()
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)

  return (
    <Feature isSelected={isSelected}>
      <p className='mc-text-x-small mc-text--uppercase mc-text--bold mc-text-color--light'>
        {t(`stackedTiles.${TierHierarchy.FAMILY_PLUS}.certificates.label`)}
      </p>
      <p className='mc-text-small'>
        <Trans
          ns={MC_PRO_TIER_I18N_NAMESPACE}
          i18nKey={`stackedTiles.${TierHierarchy.FAMILY_PLUS}.certificates.${
            value ? 'body' : 'bodyWithoutValue'
          }`}
          values={{ value }}
          components={{
            learnMore: (
              <button
                type='button'
                className={cn('mc-text--link', classes.learnMore)}
                onClick={openCertificatesFaq}
              />
            ),
          }}
        />
      </p>
    </Feature>
  )
}

export const Tile = ({
  tier,
  badge,
  tabIndex,
  onKeyDown,
}: {
  tier: PlansTier
  badge?: TileBadge
  tabIndex: number
  onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>
}) => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)
  const { t: tDevices } = useBenefitTranslation({
    name: 'concurrent_devices',
    ns: MC_PRO_TIER_I18N_NAMESPACE,
  })
  const {
    selectedPlan,
    onClickIndividual,
    onClickDuo,
    onClickFamily,
    onClickFamilyPlus,
  } = usePlansContext()
  const ACTIONS = {
    [TierHierarchy.INDIVIDUAL]: onClickIndividual,
    [TierHierarchy.DUO]: onClickDuo,
    [TierHierarchy.FAMILY]: onClickFamily,
    [TierHierarchy.FAMILY_PLUS]: onClickFamilyPlus,
  }
  const isSelected = tier.productId === selectedPlan?.productId
  const isPro = tier.hierarchy === TierHierarchy.FAMILY_PLUS
  const instructors = getNumberOfInstructors()
  const getCopy = (section: 'subtitles' | 'features') =>
    COPY_KEYS[tier.hierarchy][section]
      .map((key) => {
        const i18nKey = `stackedTiles.${tier.hierarchy}.${section}.${key}`
        const text = DEVICE_COPY_KEYS.includes(key)
          ? tDevices(i18nKey, { tier, instructors })
          : t(i18nKey, { instructors })
        return { key, text }
      })
      .filter(({ text }) => text)
  const subtitles = getCopy('subtitles')
  const features = getCopy('features')
  const nameId = useId()
  const descriptionId = useId()
  const Name = () => (
    <h3 id={nameId} className='mc-text-h4'>
      {tier.displayName}
    </h3>
  )

  return (
    <div
      className={cn(
        'position-relative mc-overflow--hidden mc-corners--lg',
        classes.tile,
        {
          [classes.tileSelected]: isSelected,
          [classes.pro]: isPro,
        },
      )}
    >
      <div
        className={cn('d-flex flex-column mc-gap-2 mc-px-7', {
          [classes.proHeader]: isPro,
          'mc-text-color--light': isSelected,
          'mc-text-color--tint': !isSelected,
          'mc-pt-7': !isPro,
          'mc-py-5': isPro,
        })}
      >
        <div className='d-flex justify-content-between align-items-center'>
          {badge ? (
            <Badge kind={badge.kind} className='mc-text--bold'>
              {t(badge.key)}
            </Badge>
          ) : (
            <Name />
          )}
          <button
            type='button'
            role='radio'
            aria-checked={isSelected}
            aria-labelledby={nameId}
            aria-describedby={descriptionId}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
            className={cn('flex-shrink-0 mc-corners--circle', classes.radio, {
              [classes.radioSelected]: isSelected,
            })}
            onClick={ACTIONS[tier.hierarchy]}
          />
        </div>
        {badge && <Name />}
        <div
          id={descriptionId}
          className='d-flex flex-column mc-gap-1 mc-text-small'
        >
          {subtitles.map(({ key, text }) => (
            <p key={key}>{text}</p>
          ))}
        </div>
        <p className={isPro ? 'mc-text-small' : ''}>
          <Trans
            ns={MC_PRO_TIER_I18N_NAMESPACE}
            i18nKey='stackedTiles.price'
            values={{ price: tier.displayMonthlyPrice }}
            components={{
              periodPrice: <span className={isPro ? '' : 'mc-text--bold'} />,
              price: <span className={isPro ? 'mc-text-h2' : ''} />,
            }}
          />
        </p>
      </div>

      <div className={cn('mc-px-7 mc-pb-7', { 'mc-mt-7': isPro })}>
        {!isPro && <Separator kind='subtle' className='mc-my-7' />}
        <ul className='d-flex flex-column mc-gap-3'>
          {features.map(({ key, text }) => (
            <Feature key={key} isSelected={isSelected}>
              <p>{text}</p>
            </Feature>
          ))}
          {isPro && <CertificatesFeature isSelected={isSelected} />}
        </ul>
      </div>
    </div>
  )
}
